import {Injectable, OnDestroy} from "@angular/core";
import {SelfSchedulingDataService} from "./self.scheduling.data.service";
import {ILocationTreeNode, ISelfLocation} from "../interfaces/Iself.scheduling";
import {
	daysData,
	FilterType,
	IStackedViewFilterChip,
	timeOfDayData,
} from "../components/subpages/self.schedule.subpage/self.schedule.filters/self.schedule.filters.data";
import * as moment from 'moment';
import {Moment} from 'moment';
import {SystemConfigurationValues} from "../../../common/configuration/services/system.configuration.manager";
import * as _ from "lodash";
import {TranslateService} from "../../../common/translate/services/translate.service";
import {KendoDateTimeService} from "../../../common/datetime/services/kendo.datetime.service";

@Injectable({providedIn: "root"})
export class SelfSchedulingFiltersService implements OnDestroy {

	public stackedViewFilterItems: IStackedViewFilterChip[] = [];

	public lastLocationTreeCheckState: { [orgKey: number]: boolean; } = {};

	public startOfWeekDayIdx;
	public timeOfDayData: any[] = [];

	constructor(public selfSchedulingDataService: SelfSchedulingDataService, public cfg: SystemConfigurationValues,
							private translateService: TranslateService, private kendoDateTimeService: KendoDateTimeService) {

		timeOfDayData.forEach(tod => {
			const timeRangeString = cfg.getValidatedString(tod.config);
			this.startOfWeekDayIdx = cfg.PP_CalendarFirstDayOfWeek;

			if (timeRangeString && timeRangeString.length > 0) {
				let validTimeRange = this.extractValidDateRange(timeRangeString);

				tod.startTime = validTimeRange ? validTimeRange.from : tod.startTime;
				tod.endTime = validTimeRange ? validTimeRange.to : tod.endTime;
				this.timeOfDayData.push(tod);
			}});

		this.startOfWeekDayIdx = cfg.PP_CalendarFirstDayOfWeek;
	}

	public removeFilter(filter: IStackedViewFilterChip) {

		switch (filter.type) {
			case FilterType.DayFilter:
				this.selfSchedulingDataService.dateTimeOptions.weekdays[filter.item] = false;
				this.selfSchedulingDataService.dateTimeOptions.weekdays =
						this.selfSchedulingDataService.dateTimeOptions.weekdays.some(weekday => weekday) ?
								[...this.selfSchedulingDataService.dateTimeOptions.weekdays] : null;
				break;
			case FilterType.TimeRangeFilter:
				this.selfSchedulingDataService.dateTimeOptions.startTime = null;
				this.selfSchedulingDataService.dateTimeOptions.endTime = null;
				break;
			case FilterType.LocationFilter:
				this.selfSchedulingDataService.selectedLocations =
						this.selfSchedulingDataService.selectedLocations.filter(location => location.orgStructureKey !== filter.item.orgStructureKey);

				this.lastLocationTreeCheckState[filter.item.orgStructureKey] = false;

				this.toggleAllChildLocationNodes(
						this.selfSchedulingDataService.locationsTree,
						this.lastLocationTreeCheckState,
						filter.item.orgStructureKey,
						false
				);
				break;
		}

		// remove filter chip from the stacked view item list.
		this.stackedViewFilterItems = this.stackedViewFilterItems.filter(stackedViewItem => stackedViewItem !== filter);
	}

	public setSelectedLocationByCheckedState(locationSelectionCheckedState: { [orgKey: number]: boolean }) {
		this.lastLocationTreeCheckState = locationSelectionCheckedState;

		const selectedLocationsOrgKeys = _.keys(locationSelectionCheckedState)
				.filter(orgKey => locationSelectionCheckedState[orgKey])
				.map(orgKeyString => +orgKeyString);

		this.setSelectedLocationsByOrgKeys(selectedLocationsOrgKeys);
	}

	public setSelectedLocationsByOrgKeys(selectedLocationsOrgKeys: number[]) {
		const listOfTopSelectedLocations: ISelfLocation[] = [];

		this.selfSchedulingDataService.locationsTree.forEach(rootNode => {
			const result = this.getTopMostIterateChildren(rootNode, selectedLocationsOrgKeys);

			if (result) { listOfTopSelectedLocations.push(...result); }
		});

		this.selfSchedulingDataService.selectedLocations = listOfTopSelectedLocations;

		this.stackedViewFilterItems = [
			...this.selfSchedulingDataService.selectedLocations.map((location) => ({
				item: location,
				type: FilterType.LocationFilter,
				description: location.description
			})),
			...this.stackedViewFilterItems.filter(item => item.type !== FilterType.LocationFilter)
		];
	}

	public setTimeRangeFilter(from: Date, to: Date) {
		this.selfSchedulingDataService.dateTimeOptions.startTime = from;
		this.selfSchedulingDataService.dateTimeOptions.endTime = to;

		// update time range filter chips
		if (this.selfSchedulingDataService.dateTimeOptions.startTime && this.selfSchedulingDataService.dateTimeOptions.endTime) {
			this.stackedViewFilterItems = [
				...this.stackedViewFilterItems.filter(item => item.type !== FilterType.TimeRangeFilter),
				{
					item: {
						fromTime: this.selfSchedulingDataService.dateTimeOptions.startTime,
						toTime: this.selfSchedulingDataService.dateTimeOptions.endTime
					},
					type: FilterType.TimeRangeFilter,
					description: ""
				}
			];
		}
	}

	public setSelectedDaysFilter(selectedDays: boolean[]) {
		this.selfSchedulingDataService.dateTimeOptions.weekdays = selectedDays?.some(day => day) ? [...selectedDays] : null;

		// update selected days filter chips
		if (this.selfSchedulingDataService.dateTimeOptions.weekdays) {
			this.stackedViewFilterItems = [
				...this.stackedViewFilterItems,
				...this.selfSchedulingDataService.dateTimeOptions.weekdays
					.map((weekday, index) => ({ weekday, index }))
					.filter(({ weekday, index }) => weekday && !this.stackedViewFilterItems
						.some(filterChip => filterChip.type === FilterType.DayFilter && filterChip.item === index))
					.map(({ weekday, index }) => ({
						item: index,
						type: FilterType.DayFilter,
						description: daysData[index].caption
					}))
			];
		}

		// update reference for change detection
		// this.stackedViewFilterItems = [...this.stackedViewFilterItems];
	}

	private getTopMostIterateChildren(currentNode: ILocationTreeNode, selectedOrgKeys: number[]): ISelfLocation[] {
		const topMostSelectedLocations: ISelfLocation[] = [];

		if (selectedOrgKeys.indexOf(currentNode.location.orgStructureKey) !== -1) {
			topMostSelectedLocations.push(currentNode.location);
			return topMostSelectedLocations;

		} else {
			if (currentNode.children && currentNode.children.length > 0) {
				currentNode.children.forEach(childNode => {
					const result = this.getTopMostIterateChildren(childNode, selectedOrgKeys);
					if (result) {
						topMostSelectedLocations.push(...result);
					}
				});

				if (topMostSelectedLocations.length > 0) {
					return topMostSelectedLocations;
				}
			}
		}
	}

	public getTopMostSelectedLocations(selectedLocationsOrgKeys: number[]): ISelfLocation[] {
		const listOfTopSelectedLocations: ISelfLocation[] = [];

		this.selfSchedulingDataService.locationsTree.forEach(rootNode => {
			const result = this.getTopMostIterateChildren(rootNode, selectedLocationsOrgKeys);

			if (result) { listOfTopSelectedLocations.push(...result); }
		});

		return listOfTopSelectedLocations;
	}

	private extractValidDateRange(dateRangeString: string): {from: Date, to: Date} {
		let timeRange: Moment[];
		if (dateRangeString && dateRangeString.replace(/\s/g, '').split('-').length === 2) {
			timeRange = dateRangeString.replace(/\s/g, '').split('-').map(hour => moment(hour, 'HH:mm'));

			if (timeRange && timeRange[0] && timeRange[0].isValid() &&
					timeRange[1] && timeRange[1].isValid()) {

				if (!((timeRange[0].hour() >= 24 && timeRange[1].hour() <= 24 ) || timeRange[1].isBefore(timeRange[0]))) {
					return {from: timeRange[0].toDate(), to: timeRange[1].toDate()};
				}
			}
		}
	}

	public toggleAllChildLocationNodes(fullLocationTree: ILocationTreeNode[],
																		 checkStateDictionary: { [orgKey: number]: boolean; },
																		 parentOrgKey: number, toggleState: boolean) {

		fullLocationTree.forEach(locationTreeRootNode => {
			this.findTreeNodeAndToggleChildren(parentOrgKey, locationTreeRootNode, checkStateDictionary, toggleState);
		});
	}

	private findTreeNodeAndToggleChildren(orgKey: number, currentTreeNode: ILocationTreeNode,
																				checkStateDictionary: { [orgKey: number]: boolean; }, toggleState: boolean) {
		if (currentTreeNode && currentTreeNode.location.orgStructureKey === orgKey) {
			if (currentTreeNode.children && currentTreeNode.children.length > 0) {
				currentTreeNode.children.forEach(childNode => this.toggleChildren(childNode, checkStateDictionary, toggleState));
			}
		} else if (currentTreeNode && currentTreeNode.children && currentTreeNode.children.length > 0) {
			currentTreeNode.children.forEach(childNode => {
				this.findTreeNodeAndToggleChildren(orgKey, childNode, checkStateDictionary, toggleState);
			});
		}
	}

	private toggleChildren(currentNode: ILocationTreeNode, checkStateDictionary: { [orgKey: number]: boolean; },
												 state: boolean) {
		checkStateDictionary[currentNode.location.orgStructureKey] = state;

		if (currentNode.children && currentNode.children.length > 0) {
			currentNode.children.forEach(childNode => this.toggleChildren(childNode, checkStateDictionary, state));
		}
	}

	public clearData() {
		this.stackedViewFilterItems = [];
		this.lastLocationTreeCheckState = {};
	}

	public clearLocationData() {
		this.stackedViewFilterItems = [...this.stackedViewFilterItems.filter(item => item.type !== FilterType.LocationFilter)];
		this.selfSchedulingDataService.selectedLocations = [];
		this.lastLocationTreeCheckState = {};
	}

	ngOnDestroy(): void {
	}
}
